import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { cn } from '@repo/ui/utils';
import ErrorPage from './ErrorPage';

const CHUNK_LOAD_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
];

const isChunkLoadError = (error: unknown) =>
  error instanceof Error &&
  CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => error.message.includes(pattern));

const reload = () => window.location.reload();

export const RouteErrorBoundary = () => {
  const error = useRouteError();
  const chunkLoadError = isChunkLoadError(error);
  const offline = !navigator.onLine;

  useEffect(() => {
    if (chunkLoadError && offline) return;
    Sentry.captureException(error);
  }, [error, chunkLoadError, offline]);

  useEffect(() => {
    if (!chunkLoadError) return;
    window.addEventListener('online', reload);
    return () => window.removeEventListener('online', reload);
  }, [chunkLoadError]);

  const { title, description } = offline
    ? {
        title: "You're offline",
        description:
          'Check your internet connection. The page will reload automatically once you are back online.',
      }
    : chunkLoadError
      ? {
          title: 'This page could not be loaded',
          description:
            'There may be a connection problem or a new version of the app is available. Please reload the page.',
        }
      : {
          title: 'Something went wrong',
          description:
            'An unexpected error occurred. Please reload the page. If the problem persists, contact support.',
        };

  return (
    <main
      id="main-content"
      tabIndex={-1}
      role="alert"
      className={cn('flex', 'min-h-screen', 'items-center', 'justify-center')}
    >
      <ErrorPage
        title={title}
        description={description}
        action={{ label: 'Reload page', onClick: reload }}
      />
    </main>
  );
};

export default RouteErrorBoundary;
